import { useCallback, useEffect } from 'react';

import { useAppDispatch, useFeatureFlagSplit } from '@verantos-dev/app-state-base';
import { updateCohortBuilderSliceState } from '@verantos-dev/app-state-cohort-builder';
import { setCohortDefinitionLegacy } from '@verantos-dev/app-state-cohort-definition';
import { transformCohortDSLStringToCohortBuilderDefinition } from '@verantos-dev/data-access-cohorts';
import { generateUIModelFromDSL } from '@verantos-dev/util-cohort-definition-dsl-to-ui';
import { generateDSLFromUIModel } from '@verantos-dev/util-cohort-definition-ui-to-dsl';

import { useResearchAssistantSocketContext } from '../../contexts/ResearchAssistantSocketContext';
import { useConceptGroupsMaps } from '../../hooks/useConceptGroupsMaps';

export const useCohortDefinitionListeners = () => {
  const dispatch = useAppDispatch();

  const { socket: currentSocket } = useResearchAssistantSocketContext();
  const isAgentJsonFeature = useFeatureFlagSplit('agent_json') as boolean;
  const restrictSaveProcess = useFeatureFlagSplit('restrict_save_process_on_error', false);

  const { _mapConceptGroupNameToId } = useConceptGroupsMaps();

  const cohortDslHandler = useCallback(
    (message: any) => {
      if (message) {
        if (isAgentJsonFeature) {
          message = JSON.parse(message);
          message = generateDSLFromUIModel(message);
          localStorage.setItem('cohortJson', JSON.stringify(message, null, 2));
        }
        // Added to store the full cohort dsl in local storage to verify output from LLM
        localStorage.setItem('cohortDsl', JSON.stringify(message));
        let _message = message;
        _message = _mapConceptGroupNameToId(_message);
        // Added below line of code to store the full cohort dsl after mapping the concept group name to id
        // to debug the issue with the concept group mapping issue
        // add escape character for single quotes in DSL
        const regSQ = new RegExp("['’‘`´]", 'g');
        _message = _message.replace(regSQ, "'");
        localStorage.setItem('cohortDsl_after', JSON.stringify(_message));
        console.info('Generating UI model from Research Assistant DSL');
        dispatch(
          setCohortDefinitionLegacy({
            cohortDefinitionLegacy: generateUIModelFromDSL(_message, !!restrictSaveProcess),
            isStagedFromResearchAssistant: true,
          })
        );

        // Legacy runtime hook into cohort builder
        dispatch(
          updateCohortBuilderSliceState(
            transformCohortDSLStringToCohortBuilderDefinition(_message, !!restrictSaveProcess)
          )
        );
      }
    },
    [isAgentJsonFeature, _mapConceptGroupNameToId, dispatch, restrictSaveProcess]
  );

  useEffect(() => {
    if (currentSocket) {
      currentSocket.on('cohort_dsl', cohortDslHandler);
    }

    return () => {
      if (currentSocket) {
        currentSocket.off('cohort_dsl', cohortDslHandler);
      }
    };
  }, [cohortDslHandler, currentSocket]);
};
